import { authFetch } from '../utils/authFetch';
import { CustomerCategory, CustomerCategoriesResponse } from '../types/customerCategories';

const API_BASE = (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + '/api/v1';

export const customerCategoriesService = {
  // Get all customer categories
  async getCustomerCategories(
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<CustomerCategory[]> {
    const response = await authFetch(`${API_BASE}/customer-categories`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch customer categories');
    }
    const data: CustomerCategoriesResponse = await response.json();
    return data.data;
  },

  // Get customer category by ID
  async getCustomerCategory(
    id: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<CustomerCategory> {
    const response = await authFetch(`${API_BASE}/customer-categories/${id}`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch customer category');
    }
    const data = await response.json();
    return data.data;
  },

  // Get customer category by name
  async getCustomerCategoryByName(
    name: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<CustomerCategory> {
    const response = await authFetch(`${API_BASE}/customer-categories/by-name/${encodeURIComponent(name)}`, {}, accessToken, setUserAndToken);
    if (!response.ok) {
      throw new Error('Failed to fetch customer category by name');
    }
    const data = await response.json();
    return data.data;
  },

  // ===== Creation APIs =====

  async createCustomerCategory(
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void,
    body: { 
      name: string; 
      description?: string; 
      is_default?: boolean;
      entries?: Array<{ key: string; value: any }>;
    }
  ): Promise<CustomerCategory> {
    const response = await authFetch(`${API_BASE}/customer-categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }, accessToken, setUserAndToken);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to create category');
    }
    const data = await response.json();
    return data.data;
  },

  // ===== Entry Creation APIs =====

  async createCategoryEntries(
    id: string,
    entries: Array<{ key: string; value: any }>,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<CustomerCategory> {
    const response = await authFetch(`${API_BASE}/customer-categories/${id}/entries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entries })
    }, accessToken, setUserAndToken);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to create category entries');
    }
    const data = await response.json();
    return data.data;
  },

  // ===== Editing APIs =====

  async updateCategoryBasics(
    id: string,
    body: { name?: string; description?: string; is_default?: boolean; order_to_evaluate?: number },
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<CustomerCategory> {
    const response = await authFetch(`${API_BASE}/customer-categories/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }, accessToken, setUserAndToken);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to update category');
    }
    const data = await response.json();
    return data.data;
  },


  async deleteCategoryEntry(
    id: string,
    keyPath: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<CustomerCategory> {
    const response = await authFetch(`${API_BASE}/customer-categories/${id}/entries/${encodeURIComponent(keyPath)}`, {
      method: 'DELETE'
    }, accessToken, setUserAndToken);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to delete category entry');
    }
    const data = await response.json();
    return data.data;
  },
};
